<script>
import icon from '@/assets/images/polysigma-icon.png'
import { METHODS } from '@/util/content/marketing.js'

// Figure 2: the logo, explained. Each of its speed lines is one line of
// evidence, and they all run into the Sigma: the sum. The diagram is
// decorative (the descriptions below carry the content), so it's hidden
// from assistive tech.
export default {
    name: 'EvidenceFusion',
    data() {
        return {
            icon,
            methods: METHODS,
        }
    },
}
</script>

<template>
    <figure class="ps-fusion mb-0">
        <div class="ps-fusion__diagram" aria-hidden="true">
            <ul class="ps-fusion__rows list-unstyled mb-0">
                <li v-for="method in methods" :key="method.id" class="ps-fusion__row">
                    <span class="ps-fusion__label">{{ method.name }}</span>
                    <span class="ps-fusion__streak"></span>
                </li>
            </ul>
            <b-img :src="icon" alt="" class="ps-fusion__sigma" />
        </div>

        <b-row class="ps-fusion__methods">
            <b-col v-for="method in methods" :key="method.id" md="6" lg="3" class="ps-fusion__method">
                <p class="ps-fusion__field mb-1">{{ method.field }}</p>
                <h3 class="ps-title">{{ method.name }}</h3>
                <p class="text-muted mb-0">{{ method.body }}</p>
            </b-col>
        </b-row>

        <figcaption class="ps-figcaption mt-4">
            <strong>Figure 2.</strong>
            Four independent analyses feed one classification. An image engineered to pass one of them still has to pass the other three.
        </figcaption>
    </figure>
</template>

<style scoped lang="scss">
// --- Diagram: streaks running into the Sigma, like the logo ------------------
.ps-fusion__diagram {
    position: relative;
    display: flex;
    align-items: center;
    margin-bottom: 2.5rem;
}

.ps-fusion__rows {
    flex: 1 1 auto;
    min-width: 0;
}

.ps-fusion__row {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    height: 2.25rem;
}

.ps-fusion__label {
    display: none;
}

.ps-fusion__streak {
    height: 0.45rem;
    margin-right: -0.75rem;
    border-radius: 999px;
    background: linear-gradient(90deg, transparent 0%, var(--ps-volt) 50%, #3f7cff 100%);
}

// Lengths and weights vary like the logo's own lines: longest through the middle
.ps-fusion__row:nth-child(1) .ps-fusion__streak {
    width: 45%;
    height: 0.3rem;
}
.ps-fusion__row:nth-child(2) .ps-fusion__streak {
    width: 75%;
}
.ps-fusion__row:nth-child(3) .ps-fusion__streak {
    width: 100%;
    height: 0.6rem;
}
.ps-fusion__row:nth-child(4) .ps-fusion__streak {
    width: 60%;
    height: 0.35rem;
}

.ps-fusion__sigma {
    position: relative;
    flex: 0 0 auto;
    width: 9rem;
    height: auto;
}

// --- Method descriptions -----------------------------------------------------
.ps-fusion__method {
    padding-top: 1.5rem;
    padding-bottom: 2rem;
}

// Rule drawn inside the gutters so it lines up with the text, not the column edge
.ps-fusion__method::before {
    content: '';
    position: absolute;
    top: 0;
    left: 16px;
    right: 16px;
    border-top: 1px solid var(--ps-rule);
}

.ps-fusion__field {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--ps-real-text);
}

// Wide screens: each streak is labeled with its method, and the Sigma
// grows to span all four rows.
@media (min-width: 768px) {
    .ps-fusion__row {
        height: 3rem;
    }

    .ps-fusion__label {
        display: block;
        flex: 0 0 auto;
        margin-right: auto;
        padding-right: 1.5rem;
        font-weight: 650;
        color: var(--ps-haze);
        white-space: nowrap;
    }

    .ps-fusion__streak {
        flex: 0 1 auto;
        margin-right: -1.25rem;
    }

    .ps-fusion__row:nth-child(1) .ps-fusion__streak {
        width: 38%;
        height: 0.45rem;
    }
    .ps-fusion__row:nth-child(2) .ps-fusion__streak {
        width: 62%;
        height: 0.75rem;
    }
    .ps-fusion__row:nth-child(3) .ps-fusion__streak {
        width: 78%;
        height: 1rem;
    }
    .ps-fusion__row:nth-child(4) .ps-fusion__streak {
        width: 50%;
        height: 0.55rem;
    }

    .ps-fusion__sigma {
        width: 14rem;
    }
}

@media (min-width: 1200px) {
    .ps-fusion__sigma {
        width: 16rem;
    }

    .ps-fusion__row {
        height: 3.4rem;
    }
}
</style>
